import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { redirectToCheckout, startCheckout as startServerCheckout } from '../services/checkoutService'
import { trackCriticalEvent } from '../utils/mixpanelTracking'
import { trackCheckoutStarted } from '../utils/analytics'

export type CheckoutState =
  | 'idle'
  | 'starting' // creating the checkout session
  | 'redirecting' // handing off to the payment provider
  | 'confirming' // back from checkout, polling the scan row for is_unlocked
  | 'still_locked' // checkout cancelled / failed — scan stays locked
  | 'timed_out' // polled for 60s without is_unlocked — scan stays locked
  | 'error'

/**
 * Routing hand-off only: which scan to return to after the provider redirect.
 * It never grants access — unlock state is always read from the scans row.
 */
export const PENDING_UNLOCK_KEY = 'td_pending_unlock_scan'

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 60_000

interface UseReportUnlockOptions {
  scanId: string | null
  /** scans.is_unlocked from the database; null while the scan is loading. */
  isUnlocked: boolean | null
  score: number
  riskLevel: string
  /** The user just came back from checkout for this scan. */
  returnedFromCheckout: boolean
  /** The provider reported the checkout as cancelled / failed / expired. */
  checkoutAbandoned: boolean
  onUnlocked: () => void
  refetch: () => void
}

/**
 * Owns the $2.99 single-report checkout for one scan. The UI unlocks only
 * when the scans row itself reports is_unlocked === true — never because of
 * a popup callback, redirect, or query parameter.
 */
export function useReportUnlock({
  scanId,
  isUnlocked,
  score,
  riskLevel,
  returnedFromCheckout,
  checkoutAbandoned,
  onUnlocked,
  refetch,
}: UseReportUnlockOptions) {
  const [state, setState] = useState<CheckoutState>(
    returnedFromCheckout ? 'confirming' : checkoutAbandoned ? 'still_locked' : 'idle',
  )
  const [error, setError] = useState<string | null>(null)

  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  const onUnlockedRef = useRef(onUnlocked)
  onUnlockedRef.current = onUnlocked

  const pollGenerationRef = useRef(0)
  const pollTimerRef = useRef<number | null>(null)
  const wasUnlockedRef = useRef<boolean | null>(null)
  const autoConfirmStartedRef = useRef(false)

  const stopPolling = useCallback(() => {
    pollGenerationRef.current += 1
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  /** Re-fetch this exact scan row every 2s for up to 60s. */
  const confirmPayment = useCallback(() => {
    if (!scanId) return
    stopPolling()
    const generation = pollGenerationRef.current
    const startedAt = Date.now()
    setState('confirming')
    setError(null)

    const tick = async () => {
      const { data, error: fetchError } = await supabase
        .from('scans')
        .select('id, is_unlocked')
        .eq('id', scanId)
        .single()
      if (generation !== pollGenerationRef.current) return
      if (!fetchError && data?.is_unlocked === true) {
        stopPolling()
        // Pull the full row; the unlock lands when it reports is_unlocked.
        refetchRef.current()
        return
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        stopPolling()
        setState('timed_out')
        return
      }
      pollTimerRef.current = window.setTimeout(() => void tick(), POLL_INTERVAL_MS)
    }
    void tick()
  }, [scanId, stopPolling])

  // Unlock only from the database row flipping (poll refetch or realtime).
  useEffect(() => {
    if (isUnlocked === null) return
    const previous = wasUnlockedRef.current
    wasUnlockedRef.current = isUnlocked
    if (!isUnlocked) return
    stopPolling()
    setState('idle')
    setError(null)
    if (previous === false || (previous === null && returnedFromCheckout)) {
      onUnlockedRef.current()
    }
  }, [isUnlocked, returnedFromCheckout, stopPolling])

  // Back from checkout and the row is still locked → start confirming.
  useEffect(() => {
    if (!returnedFromCheckout || autoConfirmStartedRef.current) return
    if (isUnlocked !== false) return
    autoConfirmStartedRef.current = true
    confirmPayment()
  }, [returnedFromCheckout, isUnlocked, confirmPayment])

  useEffect(() => stopPolling, [stopPolling])

  const startCheckout = useCallback(
    async (location: string) => {
      if (!scanId || state === 'starting' || state === 'redirecting' || state === 'confirming') {
        return
      }
      setState('starting')
      setError(null)
      void trackCriticalEvent('Unlock_Attempt', {
        scan_id: scanId,
        source: location,
        score,
        risk_level: riskLevel,
      })
      trackCheckoutStarted('single_scan', location)
      // Price, currency, email and metadata are decided by the server.
      const result = await startServerCheckout({ plan: 'single_scan', scanId, location })
      if (!result.ok) {
        setState('error')
        setError(result.message)
        // Already paid for (or Pro): re-read the row so the UI reflects the database.
        if (result.code === 'already_unlocked' || result.code === 'already_pro') {
          refetchRef.current()
        }
        return
      }
      try {
        localStorage.setItem(
          PENDING_UNLOCK_KEY,
          JSON.stringify({ scanId, startedAt: Date.now() }),
        )
      } catch {
        // Storage unavailable — the return URL also carries scan_id.
      }
      setState('redirecting')
      redirectToCheckout(result.authorizationUrl)
    },
    [scanId, state, score, riskLevel],
  )

  return { state, error, startCheckout, checkAgain: confirmPayment }
}

/** Reads (without clearing) the scan the user was unlocking before checkout. */
export function readPendingUnlockScanId(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_UNLOCK_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { scanId?: string; startedAt?: number }
    if (!parsed.scanId) return null
    // Ignore stale hand-offs older than a day.
    if (parsed.startedAt && Date.now() - parsed.startedAt > 86_400_000) {
      return null
    }
    return parsed.scanId
  } catch {
    return null
  }
}

export function clearPendingUnlockScanId() {
  try {
    localStorage.removeItem(PENDING_UNLOCK_KEY)
  } catch {
    // ignore
  }
}
